import { createContext, useState, useEffect, useContext } from 'react';
import { flushSync } from 'react-dom';
import { getPreferredTemplate } from '../api/user';
import { getAtozasSession, logoutAtozasSession, startAtozasLogin } from '../api/auth';
import { isAtozasProductEntry, ssoReturnToFromLocation } from '../utils/atozasEntry';

export const SSO_LOGGED_OUT_KEY = 'sso_logged_out';
const SSO_SESSION_KEY = 'sso_session';

const AuthContext = createContext();

const readStoredAuth = () => {
  try {
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    if (!savedToken || !savedUser) return { token: null, user: null };
    const parsedUser = JSON.parse(savedUser);
    if (!parsedUser || typeof parsedUser !== 'object') return { token: null, user: null };
    return {
      token: savedToken,
      user: { ...parsedUser, id: parsedUser.id || parsedUser._id || null },
    };
  } catch {
    return { token: null, user: null };
  }
};

const initialAuth = readStoredAuth();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(initialAuth.user);
  const [token, setToken] = useState(initialAuth.token);
  const [isLoading, setIsLoading] = useState(!initialAuth.user);

  // Validate token by making an authenticated API call
  const validateToken = async (authToken) => {
    if (!authToken) return false;
    
    try {
      // Use a lightweight endpoint to validate token
      await getPreferredTemplate(authToken);
      return true;
    } catch (error) {
      // Token is invalid or expired
        if (error.response?.status === 401) {
        return false;
      }
      // For other errors, assume token is still valid (network issues, etc.)
      return true;
    }
  };

  const applySsoUser = (sso) => {
    sessionStorage.removeItem(SSO_LOGGED_OUT_KEY);
    sessionStorage.setItem(SSO_SESSION_KEY, '1');
    const normalizedSsoUser = {
      ...sso.user,
      id: sso.user.id || sso.user._id || null,
    };
    setToken(sso.token);
    setCurrentUser(normalizedSsoUser);
    localStorage.setItem('authToken', sso.token);
    localStorage.setItem('user', JSON.stringify(normalizedSsoUser));
  };

  const clearLocalAuth = () => {
    sessionStorage.removeItem(SSO_SESSION_KEY);
    setToken(null);
    setCurrentUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  };

  useEffect(() => {
    const restoreLocalJwt = async () => {
      const savedToken = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');
      if (!savedToken || !savedUser) return false;

      try {
        const parsedUser = JSON.parse(savedUser);
        const normalizedUser = parsedUser && typeof parsedUser === 'object'
          ? { ...parsedUser, id: parsedUser.id || parsedUser._id || null }
          : null;
        if (!normalizedUser) {
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          return false;
        }
        const isValid = await validateToken(savedToken);
        if (isValid) {
          setToken(savedToken);
          setCurrentUser(normalizedUser);
          localStorage.setItem('user', JSON.stringify(normalizedUser));
          return true;
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      } catch (err) {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
      }
      return false;
    };

    const applyGoogleHashSession = () => {
      const hash = window.location.hash || "";
      const prefix = "#google_session=";
      if (!hash.startsWith(prefix)) return false;
      try {
        const data = JSON.parse(decodeURIComponent(hash.slice(prefix.length)));
        if (!data?.token || !data?.user) return false;
        sessionStorage.removeItem(SSO_LOGGED_OUT_KEY);
        sessionStorage.removeItem(SSO_SESSION_KEY);
        const normalizedUser = {
          ...data.user,
          id: data.user.id || data.user._id || null,
        };
        setToken(data.token);
        setCurrentUser(normalizedUser);
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        const cleanUrl = `${window.location.pathname}${window.location.search}`;
        window.history.replaceState(null, "", cleanUrl);
        return true;
      } catch {
        return false;
      }
    };

    const initializeAuth = async () => {
      if (applyGoogleHashSession()) {
        setIsLoading(false);
        return;
      }

      const atozasEntry = isAtozasProductEntry();
      if (atozasEntry && !window.location.pathname.startsWith('/auth')) {
        const params = new URLSearchParams(window.location.search);
        startAtozasLogin(ssoReturnToFromLocation(), params.get('homepage_key') || undefined);
        return;
      }

      try {
        const sso = await getAtozasSession();
        if (sso?.authenticated && sso.token && sso.user) {
          applySsoUser(sso);
          setIsLoading(false);
          return;
        }

        // Only drop a previous ATOZAS session. Do not wipe Email OTP / Google JWTs
        // just because company SSO is enabled on the server.
        if (sso?.enabled && sessionStorage.getItem(SSO_SESSION_KEY) === '1') {
          clearLocalAuth();
          setIsLoading(false);
          return;
        }
      } catch {
        // Existing Email OTP / Google / password login still works without SSO.
      }

      await restoreLocalJwt();
      setIsLoading(false);
    };

    initializeAuth();

    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === 'user') {
        if (!e.newValue) {
          setToken(null);
          setCurrentUser(null);
        } else if (e.key === 'authToken') {
          setToken(e.newValue);
        } else if (e.key === 'user') {
          try {
            const parsedUser = JSON.parse(e.newValue);
            const normalizedUser = parsedUser && typeof parsedUser === 'object'
              ? { ...parsedUser, id: parsedUser.id || parsedUser._id || null }
              : null;
            setCurrentUser(normalizedUser);
          } catch {
            // Ignore malformed user payloads from other tabs.
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleTokenCleared = () => {
      setToken(null);
      setCurrentUser(null);
    };

    window.addEventListener('authTokenCleared', handleTokenCleared);

    const syncSsoOnFocus = async () => {
      try {
        const sso = await getAtozasSession();
        if (sso?.authenticated && sso.token && sso.user) {
          applySsoUser(sso);
          return;
        }
        if (sso?.enabled && sessionStorage.getItem(SSO_SESSION_KEY) === '1') {
          clearLocalAuth();
        }
      } catch {
        // Ignore transient SSO checks.
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncSsoOnFocus();
      }
    };

    window.addEventListener('focus', syncSsoOnFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authTokenCleared', handleTokenCleared);
      window.removeEventListener('focus', syncSsoOnFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const login = (userData, authToken) => {
    if (!userData || typeof userData !== 'object') return;

    sessionStorage.removeItem(SSO_LOGGED_OUT_KEY);
    sessionStorage.removeItem(SSO_SESSION_KEY);
    const normalizedUser = { ...userData, id: userData._id || userData.id };
    setCurrentUser(normalizedUser);
    setToken(authToken);
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  const logout = async (onFinished) => {
    sessionStorage.setItem(SSO_LOGGED_OUT_KEY, '1');
    sessionStorage.removeItem(SSO_SESSION_KEY);
    await Promise.all([
      logoutAtozasSession(),
      import('../pages/Login/Login'),
    ]);
    const finish = () => {
      setCurrentUser(null);
      setToken(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      onFinished?.();
    };
    if (typeof onFinished === 'function') {
      flushSync(finish);
    } else {
      finish();
    }
    return { federated: false };
  };

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      token, 
      login, 
      logout,
      isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
};
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
